/**
 * Approval API hooks for Studio.
 * Provides React hooks for interacting with the promotion approval API.
 */

import { useState, useCallback, useEffect, useRef } from "react";

// ============================================================================
// Types
// ============================================================================

/** Config type enum matching API */
export type ApprovalConfigType = "schema" | "eligibility" | "ranking" | "journey" | "branding";

/** Promotion status from the API */
export type PromotionStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "executed"
  | "failed"
  | "cancelled";

/** Source/target environment types */
export type SourceEnvironment = "dev" | "staging";
export type TargetEnvironment = "staging" | "prod";

/** Create promotion request body */
export interface CreatePromotionRequest {
  readonly verticalKey: string;
  readonly configTypes: readonly ApprovalConfigType[];
  readonly sourceEnvironment: SourceEnvironment;
  readonly targetEnvironment: TargetEnvironment;
  readonly sourceVersion: number;
  readonly changeSummary: string;
}

/** Promotion response from API */
export interface PromotionResponse {
  readonly id: string;
  readonly tenantId?: string;
  readonly verticalKey: string;
  readonly configTypes: readonly string[];
  readonly sourceEnvironment: string;
  readonly targetEnvironment: string;
  readonly sourceVersion: number;
  readonly targetVersion?: number;
  readonly status: PromotionStatus;
  readonly changeSummary: string;
  readonly requestedBy: string;
  readonly requestedAt: string;
  readonly reviewedBy?: string;
  readonly reviewedAt?: string;
  readonly reviewNotes?: string;
  readonly executedBy?: string;
  readonly executedAt?: string;
  readonly errorMessage?: string;
}

/** List promotions query params */
export interface ListPromotionsParams {
  readonly status?: PromotionStatus;
  readonly verticalKey?: string;
  readonly targetEnvironment?: TargetEnvironment;
  readonly limit?: number;
  readonly offset?: number;
}

/** Pagination info from API */
export interface PromotionPagination {
  readonly limit: number;
  readonly offset: number;
  readonly hasMore: boolean;
  readonly total?: number;
}

/** List promotions response */
export interface PromotionListResponse {
  readonly data: readonly PromotionResponse[];
  readonly pagination: PromotionPagination;
}

/** API client configuration */
export interface ApprovalApiClientConfig {
  readonly baseUrl: string;
  readonly getAuthToken: () => string | null;
}

/** API error from envelope */
interface ApiErrorEnvelope {
  readonly error?: {
    readonly message?: string;
    readonly code?: string;
  };
}

// ============================================================================
// API Client
// ============================================================================

/**
 * Create an API client for the promotion approval workflow.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createApprovalApiClient(config: ApprovalApiClientConfig) {
  const { baseUrl, getAuthToken } = config;

  const fetchWithAuth = async <T>(path: string, options: RequestInit = {}): Promise<T> => {
    const token = getAuthToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      const error = data as ApiErrorEnvelope;
      throw new Error(error.error?.message ?? `API error: ${response.status}`);
    }

    return data as T;
  };

  return {
    /**
     * Request a new promotion.
     */
    requestPromotion: async (body: CreatePromotionRequest): Promise<PromotionResponse> => {
      const response = await fetchWithAuth<{ data: PromotionResponse }>("/api/v1/promotions", {
        method: "POST",
        body: JSON.stringify(body),
      });
      return response.data;
    },

    /**
     * List promotions with optional filters.
     */
    listPromotions: async (params?: ListPromotionsParams): Promise<PromotionListResponse> => {
      const searchParams = new URLSearchParams();
      if (params?.status !== undefined) searchParams.set("status", params.status);
      if (params?.verticalKey !== undefined) searchParams.set("verticalKey", params.verticalKey);
      if (params?.targetEnvironment !== undefined)
        searchParams.set("targetEnvironment", params.targetEnvironment);
      if (params?.limit !== undefined) searchParams.set("limit", params.limit.toString());
      if (params?.offset !== undefined) searchParams.set("offset", params.offset.toString());
      const query = searchParams.toString() ? `?${searchParams.toString()}` : "";
      return fetchWithAuth<PromotionListResponse>(`/api/v1/promotions${query}`);
    },

    /**
     * Get a single promotion by ID.
     */
    getPromotion: async (promotionId: string): Promise<PromotionResponse> => {
      const response = await fetchWithAuth<{ data: PromotionResponse }>(
        `/api/v1/promotions/${promotionId}`
      );
      return response.data;
    },

    /**
     * Approve a promotion request.
     */
    approvePromotion: async (promotionId: string, notes?: string): Promise<PromotionResponse> => {
      const body: Record<string, string> = {};
      if (notes !== undefined) {
        body["notes"] = notes;
      }
      const response = await fetchWithAuth<{ data: PromotionResponse }>(
        `/api/v1/promotions/${promotionId}/approve`,
        {
          method: "POST",
          body: JSON.stringify(body),
        }
      );
      return response.data;
    },

    /**
     * Reject a promotion request.
     */
    rejectPromotion: async (promotionId: string, reason: string): Promise<PromotionResponse> => {
      const response = await fetchWithAuth<{ data: PromotionResponse }>(
        `/api/v1/promotions/${promotionId}/reject`,
        {
          method: "POST",
          body: JSON.stringify({ reason }),
        }
      );
      return response.data;
    },

    /**
     * Execute an approved promotion.
     */
    executePromotion: async (promotionId: string): Promise<PromotionResponse> => {
      const response = await fetchWithAuth<{ data: PromotionResponse }>(
        `/api/v1/promotions/${promotionId}/execute`,
        {
          method: "POST",
        }
      );
      return response.data;
    },
  };
}

export type ApprovalApiClient = ReturnType<typeof createApprovalApiClient>;

// ============================================================================
// React Hooks
// ============================================================================

/** State for async operations */
interface AsyncState<T> {
  readonly data: T | null;
  readonly isLoading: boolean;
  readonly error: string | null;
}

/**
 * Hook for the approval queue — fetches and manages the list of promotions.
 */
export function useApprovalQueue(
  client: ApprovalApiClient,
  filters?: ListPromotionsParams
): {
  promotions: readonly PromotionResponse[];
  pagination: PromotionPagination | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [state, setState] = useState<AsyncState<PromotionListResponse>>({
    data: null,
    isLoading: false,
    error: null,
  });

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const clientRef = useRef(client);
  clientRef.current = client;

  const refresh = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await clientRef.current.listPromotions(filtersRef.current);
      setState({ data: result, isLoading: false, error: null });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : "Failed to load promotions",
      }));
    }
  }, []);

  // Auto-fetch on mount and when filters change
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    void refresh();
  }, [refresh, filtersKey]);

  return {
    promotions: state.data?.data ?? [],
    pagination: state.data?.pagination ?? null,
    isLoading: state.isLoading,
    error: state.error,
    refresh,
  };
}

/**
 * Hook for approval actions (approve, reject, execute).
 */
export function useApprovalActions(client: ApprovalApiClient): {
  approve: (promotionId: string, notes?: string) => Promise<PromotionResponse>;
  reject: (promotionId: string, reason: string) => Promise<PromotionResponse>;
  execute: (promotionId: string) => Promise<PromotionResponse>;
  isLoading: boolean;
  error: string | null;
} {
  const [state, setState] = useState<{
    isLoading: boolean;
    error: string | null;
  }>({
    isLoading: false,
    error: null,
  });

  const approve = useCallback(
    async (promotionId: string, notes?: string): Promise<PromotionResponse> => {
      setState({ isLoading: true, error: null });
      try {
        const result = await client.approvePromotion(promotionId, notes);
        setState({ isLoading: false, error: null });
        return result;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Approval failed";
        setState({ isLoading: false, error: errorMsg });
        throw err;
      }
    },
    [client]
  );

  const reject = useCallback(
    async (promotionId: string, reason: string): Promise<PromotionResponse> => {
      setState({ isLoading: true, error: null });
      try {
        const result = await client.rejectPromotion(promotionId, reason);
        setState({ isLoading: false, error: null });
        return result;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Rejection failed";
        setState({ isLoading: false, error: errorMsg });
        throw err;
      }
    },
    [client]
  );

  const execute = useCallback(
    async (promotionId: string): Promise<PromotionResponse> => {
      setState({ isLoading: true, error: null });
      try {
        const result = await client.executePromotion(promotionId);
        setState({ isLoading: false, error: null });
        return result;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Execution failed";
        setState({ isLoading: false, error: errorMsg });
        throw err;
      }
    },
    [client]
  );

  return {
    approve,
    reject,
    execute,
    isLoading: state.isLoading,
    error: state.error,
  };
}

/**
 * Hook for pending promotion count (notification badge).
 */
export function usePendingCount(client: ApprovalApiClient): {
  count: number;
  isLoading: boolean;
  refresh: () => Promise<void>;
} {
  const [state, setState] = useState<{
    count: number;
    isLoading: boolean;
  }>({
    count: 0,
    isLoading: false,
  });

  const clientRef = useRef(client);
  clientRef.current = client;

  const refresh = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const countResult = await clientRef.current.listPromotions({
        status: "pending",
        limit: 100,
      });
      const total = countResult.pagination?.total ?? countResult.data.length;
      setState({ count: total, isLoading: false });
    } catch {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    count: state.count,
    isLoading: state.isLoading,
    refresh,
  };
}
